<template>
  <Component
    :is="imageComponent.element"
    v-bind="imageComponent.bind"
  >
    <template
      v-if="imageComponent.element === 'picture'"
    >
      <source
        v-for="source in imageComponent.sources"
        :key="source.srcset"
        :srcset="source.srcset"
        :media="source.media"
      />
      <img
        :src="imageComponent.image.src"
        :alt="imageComponent.image.alt"
        :width="imageComponent.image.width"
        :height="imageComponent.image.height"
      />
    </template>
  </Component>
</template>

<script setup>
import {computed} from "vue";
import {
  IMAGE_QUALITY_LEVELS,
  QUALITY_AUTO,
  QUALITY_HIGH,
  QUALITY_LOW,
  QUALITY_ORIGINAL,
  QUALITY_THUMBNAIL
} from "../constants/FileQualityLevels.js";

const props = defineProps({
  qualityLevel: {
    type: String,
    default: QUALITY_AUTO,
    validator: (value) => [
      QUALITY_THUMBNAIL,
      QUALITY_LOW,
      QUALITY_HIGH,
      QUALITY_ORIGINAL,
      QUALITY_AUTO,
    ].includes(value),
  },
  image: {
    type: Object,
    bind: {},
    required: true,
  },
  loadingType: {
    type: String,
    default: "lazy",
  },
});

const getAltText = () => {
  return props.image.current_name || props.image.original_name || "Image File";
};

const getImage = (exactQualityLevel) => {
  if (!props.image[`${exactQualityLevel}_path`]) return null;

  return {
    ["data-quality"]: exactQualityLevel,
    alt: getAltText(),
    src: props.image[`${exactQualityLevel}_path`],
    width: props.image[`${exactQualityLevel}_width`],
    height: props.image[`${exactQualityLevel}_height`],
    class: "select-none",
  };
};

const getClosestImage = (preferredQualityLevel) => {
  const exactMatch = getImage(preferredQualityLevel);
  if (exactMatch) return exactMatch;

  const preferHigherQuality = IMAGE_QUALITY_LEVELS.indexOf(preferredQualityLevel) >= Math.floor(IMAGE_QUALITY_LEVELS.length / 2);
  const availableImagesIndex = preferHigherQuality ? availableImages.value.length - 1 : 0;

  return availableImages.value[availableImagesIndex];
};


const availableImages = computed(() => {
  let allImages = [];

  for (let i = 0; i < IMAGE_QUALITY_LEVELS.length; i++) {
    const qualityLevel = IMAGE_QUALITY_LEVELS[i];
    const image = getImage(qualityLevel);
    if (image) allImages.push(image);
  }

  return allImages;
});

const imageComponent = computed(() => {
  const isAutoQuality = props.qualityLevel === QUALITY_AUTO;
  const hasMultiple = Object.keys(availableImages.value).length > 1;

  if (isAutoQuality && hasMultiple) {
    const allButSmallestImage = availableImages.value.slice(0, -1);
    const allHaveWidth = allButSmallestImage.every((image) => image?.width);

    const sources = allButSmallestImage.map((data) => {
      const source = {
        srcset: data.image.src,
      };

      if (allHaveWidth) source.srcset += ` ${data.image.width}w`;
      else {
        const qualityLevel = data["data-quality"];
        let mediaMinWidthPx;

        if (qualityLevel === QUALITY_ORIGINAL) mediaMinWidthPx = 1920;
        else if (qualityLevel === QUALITY_HIGH) mediaMinWidthPx = 1024;
        else if (qualityLevel === QUALITY_LOW) mediaMinWidthPx = 320;
        else mediaMinWidthPx = 0;

        data.media = `(min-width: ${mediaMinWidthPx}px)`;
      }

      return data;
    });

    return {
      element: "picture",
      bind: {},
      sources,
      image: availableImages.value[0].image,
    };
  }

  return {
    element: "img",
    bind: getClosestImage(props.qualityLevel),
  };
});
</script>
